import React from 'react';
import { SubscriptionTier } from '../../services/SubscriptionService';

interface TierDetails {
    name: string;
    description: string;
    price: string;
    features: string[];
    isPopular?: boolean;
}

interface SubscriptionTierCardProps {
    tier: SubscriptionTier;
    details: TierDetails;
    isSelected: boolean;
    isDisabled?: boolean;
    onClick: (tier: SubscriptionTier) => void;
}

const SubscriptionTierCard: React.FC<SubscriptionTierCardProps> = ({
    tier,
    details,
    isSelected,
    isDisabled = false,
    onClick
}) => {
    const handleClick = () => {
        if (!isDisabled) {
            onClick(tier);
        }
    };

    return (
        <div 
            className={`relative p-6 rounded-xl border transition-all ${
                isDisabled 
                    ? 'cursor-not-allowed opacity-60 border-gray-200 dark:border-gray-800' 
                    : isSelected 
                        ? 'cursor-pointer border-brand-500 dark:border-brand-400 bg-brand-50 dark:bg-brand-500/[0.12]' 
                        : 'cursor-pointer border-gray-200 hover:border-brand-200 dark:border-gray-800 dark:hover:border-brand-600 hover:shadow-md'
            }`}
            onClick={handleClick}
        >
            {details.isPopular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 bg-brand-500 text-white text-xs font-medium rounded-full">
                    Popular
                </div>
            )}
            <div className="text-center mb-4">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white/90">{details.name}</h3>
                <p className="text-gray-500 dark:text-gray-400 mt-1">{details.description}</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white/90 mt-4">{details.price}</p>
            </div>
            
            <div className="space-y-3 mt-6">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Features:</h4>
                <ul className="space-y-2">
                    {details.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                            <svg 
                                className="w-5 h-5 mr-2 text-brand-500 dark:text-brand-400 mt-0.5" 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth={2} 
                                    d="M5 13l4 4L19 7" 
                                />
                            </svg>
                            <span className="text-sm text-gray-600 dark:text-gray-400">{feature}</span>
                        </li>
                    ))}
                </ul>
            </div>
            
            <div className="mt-6">
                <button
                    type="button"
                    disabled={isDisabled}
                    className={`w-full py-2.5 px-4 rounded-lg text-sm font-medium transition ${
                        isSelected 
                            ? 'bg-brand-500 text-white hover:bg-brand-600 dark:bg-brand-500 dark:text-white dark:hover:bg-brand-600' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                >
                    {isSelected ? 'Selected' : 'Select Plan'}
                </button>
            </div>
        </div>
    );
};

export default SubscriptionTierCard; 